import React, { Fragment, FunctionComponent } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { getCurrencyName } from 'helpers/currencies';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';

import { IReservationsShowProps } from './types';
import './i18n';

const ReservationsShow: FunctionComponent<IReservationsShowProps> = (
  props: IReservationsShowProps,
) => {
  const { item, showTitle = true } = props;

  return (
    <ViewContainer auth data-testid="reservations-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <div />
            <Title>{i18next.t<string>('RESERVATIONS_SHOW:TITLE')}</Title>
            <CustomIconButton
              onClick={() => history.push(`/public/procedures/forms/reservations/${item?.id}/edit`)}
            >
              <EditOutlined />
            </CustomIconButton>
          </TitleContainer>
        )}
        {item && (
          <>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:PERSON_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:LAST_NAME')}</td>
                  <td>{item.person.lastName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:NAME')}</td>
                  <td>{item.person.name}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_TYPE')}</td>
                  <td>{item.person.idCardTypeName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD')}</td>
                  <td>{item.person.idCard}</td>
                </tr>
                {item.person.idCardFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardFile} title="idCardFile" />
                    </td>
                  </tr>
                )}
                {item.person.idCardBackFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_BACK_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardBackFile} title="idCardBackFile" />
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ADDRESS')}</td>
                  <td>{item.person.address}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:AGE')}</td>
                  <td>{item.person.age}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:PHONE')}</td>
                  <td>{`${item.person.areaCode ? `(${item.person.areaCode}) ` : ''}${
                    item.person.phone
                  }`}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:COMMUNITY')}</td>
                  <td>{item.person.community}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:CIVIL_STATE')}</td>
                  <td>{item.person.civilStateName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:WEDDING_TEMPLE')}</td>
                  <td>{item.person.weddingTemple}</td>
                </tr>
                {item.person.ketubaFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:KETUBA_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.ketubaFile} title="ketubaFile" />
                    </td>
                  </tr>
                )}
                {item.person.marriageCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.person.marriageCertificateFile}
                        title="marriageCertificateFile"
                      />
                    </td>
                  </tr>
                )}
                {item.person.getFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:GET_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.getFile} title="getFile" />
                    </td>
                  </tr>
                )}
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:FAMILY_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:SPOUSE_INFO')}</td>
                  <td>{item.family.spouseInfo}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:FATHER_INFO')}</td>
                  <td>{item.family.fatherInfo}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:MOTHER_INFO')}</td>
                  <td>{item.family.motherInfo}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:CHILDREN_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:CHILDREN_INFO')}</td>
                  <td>{item.family.childrenInfo}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:LOCATION_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:CEMENTERY')}</td>
                  <td>{item.location.cementeryName || item.location.cementery}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:PLOT')}</td>
                  <td>{item.location.plot}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:MANAGER_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:NAME')}</td>
                  <td>{item.manager.name}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_TYPE')}</td>
                  <td>{item.manager.idCardTypeName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD')}</td>
                  <td>{item.manager.idCard}</td>
                </tr>
                {item.manager.idCardFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_FILE')}</td>
                    <td>
                      <FilePreview src={item.manager.idCardFile} title="idCardFile" />
                    </td>
                  </tr>
                )}
                {item.manager.idCardBackFile && (
                  <tr>
                    <td>{i18next.t<string>('RESERVATIONS_SHOW:ID_CARD_BACK_FILE')}</td>
                    <td>
                      <FilePreview src={item.manager.idCardBackFile} title="idCardBackFile" />
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:ADDRESS')}</td>
                  <td>{item.manager.address}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:PHONE')}</td>
                  <td>{`${item.manager.areaCode ? `(${item.manager.areaCode}) ` : ''}${
                    item.manager.phone
                  }`}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:RELATIONSHIP')}</td>
                  <td>{item.manager.relationship}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_SHOW:PAYMENT_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:CURRENCY')}</td>
                  <td>{getCurrencyName(item.payment.currency)}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:AMOUNT')}</td>
                  <td>{item.payment.amount}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('RESERVATIONS_SHOW:PAYMENT_METHOD')}</td>
                  <td>{item.payment.paymentMethod}</td>
                </tr>
              </tbody>
            </TableContainer>
            {item.comments && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('RESERVATIONS_SHOW:COMMENTS')}
                </Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default ReservationsShow;
